import moment from 'moment'
import 'moment/locale/en-gb'
import 'moment/locale/et'
import 'moment/locale/sv'
import 'moment/locale/fi'

export function getTimeStops(start, end, intervals = 30) {
  if(start.length === 5) start = start + ':00'
  const startTime = moment.utc(start, 'HH:mm:ss')
  if(end.length === 5) end = end + ':00'
  const endTime = moment.utc(end, 'HH:mm:ss')

  if (endTime.isBefore(startTime)) {
    endTime.add(1, 'day')
  }

  const timeStops = []

  while (startTime <= endTime) {
    timeStops.push(new moment.utc(startTime).format('HH:mm'))
    startTime.add(intervals, 'minutes')
  }
  return timeStops
}

/**
 * Which day the calendar should open on, given a remembered or requested day.
 * Falls back to `today` when the value is malformed, already past, or beyond the
 * booking window - so a stale cookie or a hand-edited ?day= cannot strand the
 * user outside the bookable range.
 */
export const resolveInitialDay = (candidate, today, daysahead = 13) => {
  if (!candidate) return today
  if (!moment.utc(candidate, 'YYYY-MM-DD', true).isValid()) return today
  const offset = moment.utc(candidate).diff(moment.utc(today), 'days')
  return offset >= 0 && offset < daysahead ? candidate : today
}

/**
 * Localised field ({fi,sv,en}) or a bare string -> a display string.
 * Real activity docs carry name/ingress as {en,fi,sv}, so a hardcoded `.fi`
 * (as ActivityCard does) is fragile. Never throws.
 */
export const pickText = (field, lang = 'fi') => {
  if (!field) return ''
  if (typeof field === 'string') return field
  if (typeof field !== 'object') return ''
  if (typeof field[lang] === 'string' && field[lang]) return field[lang]
  if (typeof field.fi === 'string' && field.fi) return field.fi
  const first = Object.keys(field)
    .map((k) => field[k])
    .find((v) => typeof v === 'string' && v)
  return first || ''
}

// A board window may look backwards or forwards, but a typo'd year must not
// render an empty screen forever.
const WINDOW_BOUND_DAYS = 366

/**
 * The window an activity board should show, from ?from= / ?days=.
 * `from` is honoured literally (never snapped); when absent or malformed the
 * window is the ISO week (Mon-Sun) containing `today`. Unlike
 * resolveInitialDay this DOES allow past dates - a board may look backwards.
 */
export const resolveWeekWindow = (fromParam, daysParam, today) => {
  let days = parseInt(daysParam, 10)
  if (!(days >= 1 && days <= 31)) days = 7

  const base =
    today && moment.utc(today, 'YYYY-MM-DD', true).isValid()
      ? moment.utc(today, 'YYYY-MM-DD')
      : moment.utc().startOf('day')

  let start =
    fromParam && moment.utc(fromParam, 'YYYY-MM-DD', true).isValid()
      ? moment.utc(fromParam, 'YYYY-MM-DD')
      : base.clone().startOf('isoWeek')

  if (Math.abs(start.diff(base, 'days')) > WINDOW_BOUND_DAYS) {
    start = base.clone().startOf('isoWeek')
  }

  return {
    from: start.format('YYYY-MM-DD'),
    to: start.clone().add(days - 1, 'days').format('YYYY-MM-DD'),
    days,
  }
}

/** '7.-13.9.' within one month, '28.9.-4.10.' across a boundary. */
export const formatWeekRange = (from, to) => {
  const f = moment.utc(from, 'YYYY-MM-DD', true)
  const t = moment.utc(to, 'YYYY-MM-DD', true)
  if (!f.isValid() || !t.isValid()) return ''
  return f.isSame(t, 'month')
    ? f.format('D.') + '–' + t.format('D.M.')
    : f.format('D.M.') + '–' + t.format('D.M.')
}

/** '10.00 - 12.00', with '(+1)' when the activity runs past midnight. */
export const formatActivityTimeRange = (activity) => {
  if (!activity || typeof activity.start !== 'string') return ''
  const s = moment.utc(activity.start, 'YYYY-MM-DDTHH:mm', true)
  if (!s.isValid()) return ''
  const e = moment.utc(activity.end, 'YYYY-MM-DDTHH:mm', true)
  if (!e.isValid()) return s.format('HH.mm')
  return (
    s.format('HH.mm') +
    ' – ' +
    e.format('HH.mm') +
    (e.isSame(s, 'day') ? '' : ' (+1)')
  )
}

/**
 * How many people are on an activity. Real docs disagree between
 * participants[] and participantscount (144 of 160 that carry both), so take
 * whichever is larger: any evidence of a signup means the session happened.
 */
export const participantEvidence = (activity) => {
  if (!activity) return 0
  const arr = Array.isArray(activity.participants) ? activity.participants.length : 0
  const num = typeof activity.participantscount === 'number' ? activity.participantscount : 0
  return Math.max(arr, num)
}

/**
 * One entry per day in the window that actually HAS activities, in date order.
 * Days with nothing on are omitted entirely - the poster's week began on
 * Tuesday because Monday was empty.
 *
 * Activity start/end are Helsinki wall-clock strings ('YYYY-MM-DDTHH:mm'), so
 * placement is a string comparison on the date part. Do NOT add timezone
 * arithmetic here: it can only move an early-morning event onto the previous
 * day. Activities are placed on their START date.
 */
export const groupActivitiesByDate = (activities, from, days, lang = 'fi') => {
  const cursor = moment.utc(from, 'YYYY-MM-DD', true)
  if (!cursor.isValid()) return []

  const byDate = {}
  for (const a of activities || []) {
    if (!a || typeof a.start !== 'string' || a.start.length < 10) continue
    const date = a.start.slice(0, 10)
    if (!byDate[date]) byDate[date] = []
    byDate[date].push(a)
  }

  const out = []
  for (let i = 0; i < days; i++) {
    const day = cursor.clone().add(i, 'days')
    const key = day.format('YYYY-MM-DD')
    const items = byDate[key]
    if (!items || items.length === 0) continue
    const loc = day.clone().locale(lang)
    out.push({
      date: key,
      weekdayLong: loc.format('dddd'),
      weekdayShort: loc.format('dd'),
      dayLabel: day.format('D.M.'),
      activities: items
        .slice()
        .sort((x, y) => (x.start < y.start ? -1 : x.start > y.start ? 1 : 0)),
    })
  }
  return out
}

const HEXCOLOR = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

const toRgb = (hex) => {
  if (typeof hex !== 'string' || !HEXCOLOR.test(hex.trim())) return null
  let h = hex.trim().replace('#', '')
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

/** WCAG relative luminance, 0 (black) to 1 (white). null for unparseable input. */
export const relativeLuminance = (hex) => {
  const rgb = toRgb(hex)
  if (!rgb) return null
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Black or white text, whichever is legible on `hex`. Defaults to white. */
export const readableOn = (hex, dark = '#111111', light = '#ffffff') => {
  const l = relativeLuminance(hex)
  if (l === null) return light
  return l > 0.55 ? dark : light
}

/**
 * Board palette from the tenant's appconfig.
 *
 * `embedtheme` on the init document is the venue's own choice and wins:
 *   embedtheme: { brand: '#0b7fab', accent: '#ffd23f', dark: false }
 *
 * `campaign.backgroundColor` / `campaign.theme` (read for the navbar at
 * TopNavigation.js:35,70-73) stay as the fallback so a tenant that already has
 * a brand colour there keeps it. That field is overloaded, though - it is also
 * handed to reactstrap's Navbar `color=` prop, which takes a Bootstrap name, so
 * some tenants hold 'dark' rather than a hex there. Anything that is not a hex
 * colour falls back to the neutral default: a venue cannot emit broken CSS by
 * typing the wrong thing into the init editor.
 */
export const resolveBoardTheme = (appconfig) => {
  const cfg = appconfig || {}
  const e = cfg.embedtheme || {}
  const c = cfg.campaign || {}
  const firstColor = (...candidates) => candidates.find((v) => toRgb(v)) || null
  const brand = firstColor(e.brand, c.backgroundColor) || '#20603a'
  const accent = firstColor(e.accent, c.accentColor) || '#ffd23f'
  const dark = typeof e.dark === 'boolean' ? e.dark : c.theme === 'dark'
  return {
    brand,
    onBrand: readableOn(brand),
    accent,
    onAccent: readableOn(accent),
    page: dark ? '#111418' : '#ffffff',
    onPage: dark ? '#f5f5f5' : '#16191d',
    muted: dark ? '#a8adb5' : '#6c757d',
  }
}

/**
 * Tenant logo, same precedence as the navbar: loc 'navigation', then
 * loc 'default'; only type 'url' renders (see INIT_DOC.md `logos[]`).
 */
export const resolveTenantLogo = (appconfig) => {
  const cfg = appconfig || {}
  const logos = Array.isArray(cfg.logos) ? cfg.logos : []
  const logo =
    logos.find((l) => l && l.loc === 'navigation') || logos.find((l) => l && l.loc === 'default')
  if (!logo || logo.type !== 'url' || !logo.value) return { src: null, alt: '' }
  return { src: logo.value, alt: logo.alt || (cfg.title && cfg.title.long) || '' }
}

export const getResources = (dailyFreeTimes, selectedvenue, appconfig) => {
  return dailyFreeTimes
    .map((slot) => {
      return slot.courts
        .filter((x) => {
          if (appconfig.categories.length > 0 && selectedvenue !== 'all') {
            return x.category === selectedvenue
          } else {
            return true
          }
        })
        .map((x) => ({
          venue: x.venue,
          id: x.id,
          courtname: x.name || x.displayName,
          category: x.category,
          order: x.order,
        }))
    })
    .flat(2)
    .reduce((acc, cur) => {
      const f = acc.find((x) => x.id === cur.id)
      if (!f) acc.push(cur)
      return acc
    }, [])
}

export const getResourceTimes = (dailyFreeTimes, selectedvenue, appconfig) => {
  return dailyFreeTimes.reduce((acc, slot) => {
    for (let court of slot.courts.filter((x) => {
      if (appconfig.categories.length > 0 && selectedvenue !== 'all') {
        return x.category === selectedvenue
      } else {
        return true
      }
    })) {
      court.start = slot.start
      court.startmoment = moment.utc(court.start)
      if (!acc[court.id]) acc[court.id] = []
      acc[court.id].push(court)
    }
    return acc
  }, {})
}

/**
 * A time on the merged `multipleconfig` court carries the real courts free for it in
 * `resourceIds`. The selection needs those plus their count: ReservationScreen shows the amount
 * picker from `amountofresources`, and the booking hands `resourceIds` to the backend, which
 * picks the real courts. Every view that turns a time into a selection goes through here.
 */
export const withMultiresource = (selection, time) => {
  if (time && Array.isArray(time.resourceIds) && time.resourceIds.length > 0) {
    selection.resourceIds = time.resourceIds
    selection.multiresource = true
    selection.amountofresources = time.resourceIds.length
  }
  return selection
}

/**
 * The free times to show for `selectedday` ('YYYY-MM-DD'), given the day's own (`resolved`, null
 * while it has not arrived) and the last set shown (`previous`).
 *
 * `previous` is only ever shown for the SAME day. It used to be shown for whatever day was last on
 * screen until the new one arrived - and a day the backend has not published yet never arrives:
 * the client's booking window moves at midnight, the free-time index catches up later. So the
 * calendar kept offering yesterday's slots under today's date, and since every slot carries its
 * own start, booking one booked the earlier day. A day with nothing yet shows nothing.
 */
export const freeTimesForDay = (resolved, previous, selectedday) => {
  if (resolved) return resolved
  if (!selectedday || !Array.isArray(previous)) return []
  return previous.filter((slot) => slot && slot.start && moment.utc(slot.start).format('YYYY-MM-DD') === selectedday)
}

export const formatSlot = (slot, duration) => {
  let t = {}
  if (duration) {
    t = slot.times.find((t) => t.duration === duration)
  } else {
    t = slot.times[0]
  }
  const s = JSON.parse(JSON.stringify(slot))
  delete s.times
  return withMultiresource({ ...s, ...t }, t)
}

// const uiBreaks = {
//   1200: 'desktop',
//   992: 'tablet',
//   768: 'mobile',
// }

export const formatResourceName = (name, width, amount, orientation) => {
  // let _break = 0
  // if (width > 1199) {
  //   _break = 3 // desktop
  // } else if (width > 991 && width < 1200) {
  //   _break = 2
  // } else if (width > 767 && width < 992) {
  //   _break = 1
  // }
  if (!name) return null
  const wc = name.split(' ').length
  if (wc > 1) return name
  if (amount < 10) return name
  if (orientation === 'vertical') {
    if (name.length < 7) return name
    let _s = name.substring(0, 2) + name.replace(/[^0-9]+/, '')
    if (_s.length > 7) return _s.split(' ')[0]
    return _s
  }
  return name
}

export const getResourceRow = (resource, times, resourceTimes, reservations) => {
  if(!times) return []
  if (!resourceTimes) return times.map((t) => ({ t, isStart: false, isVacancy: false }))
  return times.map((t) => {
    const _t = moment.utc(t, 'HH:mm')
    if (resourceTimes[resource.id] && resourceTimes[resource.id].length > 0) {
      const dateSetter = resourceTimes[resource.id][0].startmoment
      _t.year(dateSetter.year()).month(dateSetter.month()).date(dateSetter.date())
    }
    const isStart = resourceTimes[resource.id] ? resourceTimes[resource.id].find((r) => t === r.startmoment.format('HH:mm')) : false
    const isVacancy = resourceTimes[resource.id] ? (resourceTimes[resource.id].find((r) => {
      if (r.times && r.times.length > 0) {
        const duration = r.times[r.times.length - 1].duration
        const _s = moment.utc(r.startmoment)
        return _t.isBetween(_s, moment.utc(r.startmoment).add(duration, 'minutes'), null, '[)')
      } else {
        return false
      }
    })
      ? true
      : false) : false
    let isOwnDuration = false
    let isOwn = false
    if (reservations) {
      const _r = reservations.find((r) => {
        if(r.resourceId !== resource.id) return false
        const _s = moment.utc(r.start)
        const _e = moment.utc(r.end)
        return _t.isBetween(_s, _e, null, '()')
      })
      if (_r) {
        isOwn = true
        isOwnDuration = _t.isBetween(moment.utc(_r.start), moment.utc(_r.end), null, '[)')
      }
    }
    return { t, isStart, isVacancy, isOwn, isOwnDuration }
  })
}


const __getWallet = (appconfig, category) => { 
  let wallet = 'default'
  if (appconfig.accountkey.startsWith('tali-')) {
    wallet = 'tali'
    if (category && category.indexOf('taivallahti') > -1) {
      wallet = 'taivallahti'
    }
  }
  if (appconfig.accountkey.startsWith('evs-')) {
    wallet = 'aktia'
    if (category && category.indexOf('martinmäki') > -1) {
      wallet = 'martinmäki'
    }
  }
  if (appconfig.accountkey.startsWith('roimailapelikeskus-')) {
    wallet = 'roimailapelikeskus'
    if (category && category.indexOf('ulko') > -1) {
      wallet = 'rovs'
    }
  }
  if (appconfig.accountkey.startsWith('lahdentennisjasquash-')) {
    wallet = 'default'
    if (category && category.indexOf('ulkotennis') > -1) {
      wallet = 'ulkotennis'
    }
  }
  return wallet
}

export const getWallet = (appconfig, summary) => {
  let wallet = 'default'
  if (Array.isArray(summary)) {
    const hasSummaryCategory = summary.filter((item) => item.category)
    const hasActivities = summary.find((item) => item.collection === 'activities')
    const hasProshopItems = summary.find((item) => item.collection === 'proshop')
    if (hasProshopItems) {
      return wallet
    }
    if (hasSummaryCategory.length > 0) {
      return __getWallet(appconfig, hasSummaryCategory[0].category)
    } else if (!hasActivities) { 
      const _cat = summary.map((item) => item.resourcegroups[0].resources[0].category).filter((x) => x)
      if (_cat.length > 0) {
        return __getWallet(appconfig, _cat[0])
      }
    }
  } else {
    if (summary.venue && summary.venue.name) {
      return summary.venue.name.toLowerCase()
    } else if (summary.category) {
      return __getWallet(appconfig, summary.category)
    } else if (summary.collection === 'activities') {
      return __getWallet(appconfig, 'activities')
    } else if (summary.type === 'benefitcard') {
      return __getWallet(appconfig, 'benefitcard')
    }
  }
  if (summary.invoicer) {
    wallet = summary.invoicer.toLowerCase()
  }
  return wallet
}

export const getStorage = (appconfig, summary) => {
  let storage = 'default'
  let category = null
  console.log('getStorage', appconfig, summary)
  if (appconfig.accountkey.startsWith('lahdentennisjasquash-')) {
    if (Array.isArray(summary)) {
      const _cat = summary.map((item) => item.resourcegroups[0].resources[0].category).filter((x) => x)
      if (_cat.length > 0) {
        category = _cat[0]
      }
    } else {
      if (summary.category) {
        category = summary.category
      }
    }
    if (category && category.indexOf('ulkotennis') > -1) {
      storage = 'ulkotennis'
    }
  } 
  return storage
}

export const getPosterUrl = (data, preferredSize) => {
  if (!data) {
    return null
  }
  if (!preferredSize) {
    preferredSize = 600
  }
  let _optimizedPoster = null
  if (typeof data === 'object') {
    const { url, sizes } = data
    const closestSize = sizes.reduce((prev, curr) => {
      return Math.abs(curr - preferredSize) < Math.abs(prev - preferredSize) ? curr : prev
    })
    if (url.indexOf('.svg') > -1) {
      _optimizedPoster = url
    } else {
      _optimizedPoster = url
        ? url
          .split('/')
          .splice(0, url.split('/').length - 1)
          .join('/') +
        '/' +
        closestSize +
        '_' +
        url.split('/').splice(-1)
        : ''
    }
  } else if (typeof data === 'string') {
    if (data.indexOf('.svg') > -1) {
      _optimizedPoster = data
    } else {
      _optimizedPoster =
        data
          .split('/')
          .splice(0, data.split('/').length - 1)
          .join('/') +
        '/' +
        preferredSize +
        '_' +
        data.split('/').splice(-1)
    }
  }
  if (_optimizedPoster) {
    return _optimizedPoster
  } else {
    return null
  }
}

export const getOffset = (timeZone = 'UTC', date = new Date()) => {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }))
  return (tzDate.getTime() - utcDate.getTime()) / 6e4
}

const TIMEZONEOFFSET = getOffset('Europe/Helsinki')


export const MULTIRESOURCE_TYPE = 'multiresource'

// A group resource: a resource doc that is several courts shown to the customer as one. It is
// not a court - it never has bookings of its own - so it must never be listed as one.
export const isGroupResource = (resource) => !!(resource && resource.type === MULTIRESOURCE_TYPE)

/**
 * The venue's merged courts, one entry per group:
 *   { id, members: [real resource ids], court: <what the customer sees in their place> }
 *
 * Groups are resource docs of type 'multiresource' with their members in `resources`, as
 * getResources returns them. `legacy` is the older appconfig.multipleconfig - one group, typed
 * into the init doc by hand - honoured while venues move over; a group doc with the same id wins.
 */
export const multipleConfigs = (resources, legacy) => {
  const groups = []
  Object.keys(resources || {}).forEach((id) => {
    const doc = resources[id]
    if (!isGroupResource(doc) || !Array.isArray(doc.resources) || doc.resources.length === 0) return
    // The group's own display fields - name, category, order, sponsor, customer alert - so the
    // venue edits the merged court like any other; never a member's.
    const court = { ...doc, id, type: 'court' }
    delete court.resources
    groups.push({ id, members: doc.resources, court })
  })
  if (
    legacy &&
    legacy.multiresourceid &&
    Array.isArray(legacy.resources) &&
    legacy.resources.length > 0 &&
    !groups.some((g) => g.id === legacy.multiresourceid)
  ) {
    groups.push({
      id: legacy.multiresourceid,
      members: legacy.resources,
      court: {
        category: legacy.category || '',
        displayName: legacy.multiresourcename,
        name: legacy.multiresourcename,
        type: 'court',
        id: legacy.multiresourceid,
        legacy: true,
      },
    })
  }
  return groups
}

// Callers pass the groups from multipleConfigs; a bare legacy object still works.
const asGroups = (groups) => {
  if (!groups) return []
  if (Array.isArray(groups)) return groups
  return multipleConfigs({}, groups)
}

// The merged court for one start time. The legacy config carries no venue or order, so those
// come from the first member there, which keeps venue filters and column ordering placing it.
const groupCourt = (group, first, start, times) => {
  const court = { ...group.court, start, times }
  if (group.court.legacy) {
    delete court.legacy
    court.category = court.category || (first && first.category) || ''
    court.venue = first ? first.venue : undefined
    court.order = first ? first.order : undefined
  }
  return court
}

/**
 * The resources a customer-facing list may name: no group docs and no member courts of a group,
 * with each group's merged court in their place. For the views that list courts straight from
 * the resource map rather than from the free times.
 */
export const customerResources = (resources, groups) => {
  const _groups = asGroups(groups)
  const members = new Set(_groups.flatMap((g) => g.members))
  const out = {}
  Object.keys(resources || {}).forEach((id) => {
    if (isGroupResource(resources[id]) || members.has(id)) return
    out[id] = resources[id]
  })
  _groups.forEach((g) => {
    const court = groupCourt(g, (resources || {})[g.members.find((m) => resources && resources[m])], undefined, undefined)
    delete court.start
    delete court.times
    out[g.id] = court
  })
  return out
}

/**
 * Folds each group's member courts into one court per start time, in an already formatted list
 * (formatVacancies' cindex, or a server-formatted lastminute list).
 *
 * Times are merged by duration (and price, and the past-midnight `od`), each keeping the member
 * courts free for exactly that option in `resourceIds`. Merging per start time rather than per
 * vacancy matters: two members free from 18:00 until different ends are still one court at
 * 18:00, whose 60-minute option may have two places and whose 120-minute option one.
 *
 * Any court already carrying a group's id is dropped first: the free-time index should not list a
 * group, but if it ever does, its "free" times are not free courts.
 */
export const mergeMultipleCourts = (slots, groups) => {
  const _groups = asGroups(groups)
  if (_groups.length === 0 || !Array.isArray(slots)) return slots
  const groupIds = new Set(_groups.map((g) => g.id))
  return slots.map((slot) => {
    if (!slot || !Array.isArray(slot.courts)) return slot
    let courts = slot.courts.filter((c) => !groupIds.has(c.id) || !c.times || c.times.every((t) => t.resourceIds))
    _groups.forEach((group) => {
      const members = new Set(group.members)
      const memberCourts = courts.filter((c) => members.has(c.id))
      if (memberCourts.length === 0) return
      const times = []
      memberCourts.forEach((court) => {
        ;(court.times || []).forEach((time) => {
          const same = times.find((x) => x.duration === time.duration && x.price === time.price && x.od === time.od)
          if (same) {
            if (!same.resourceIds.includes(court.id)) same.resourceIds.push(court.id)
          } else {
            times.push({ ...time, resourceId: group.id, resourceIds: [court.id] })
          }
        })
      })
      times.sort((a, b) => a.duration - b.duration)
      courts = courts
        .filter((c) => !members.has(c.id))
        .concat([groupCourt(group, memberCourts[0], memberCourts[0].start, times)])
    })
    return { ...slot, courts }
  })
}

export const formatVacancies = (vacancies, dateindex, resources, all, groups) => {
  const _groups = asGroups(groups)
  const members = new Set(_groups.flatMap((g) => g.members))
  const byStartTime = {}
  for (let resourceid in resources) {
    // A group doc is never a court of its own, whether or not this caller merges: merged, its
    // court is built from the members below; unmerged (recurring sales), it does not exist.
    if (isGroupResource(resources[resourceid])) continue
    const resource = JSON.parse(JSON.stringify(resources[resourceid]))
    resource.id = resourceid
    if (vacancies[resource.id]) {
      vacancies[resource.id].forEach((v, i) => {
        if (!byStartTime[v.s])
          byStartTime[v.s] = {
            courts: [],
            start: moment.utc(dateindex + 'T' + v.s, 'YYYYMMDDTHHmm').format(),
            durations: [],
          }
        const court = {
          ...resource,
          id: resource.id,
          start: moment.utc(dateindex + 'T' + v.s, 'YYYYMMDDTHHmm').format(),
        }
        let times = []
        if (v.ex) {
          times = v.ex.map((x) => {
            byStartTime[v.s].durations.push(x.du)
            return { duration: x.du, price: x.p / 100, resourceId: resource.id }
          })
          court.times = times
        }
        if (v.e === '2359') {
          const ovdu = moment(v.e, 'HHmm').diff(moment(v.s, 'HHmm'), 'minutes')
          const vdu = Math.ceil(moment(v.e, 'HHmm').endOf('day').diff(moment(v.s, 'HHmm'), 'seconds') / 60)
          byStartTime[v.s].durations.push(vdu)
          times.push({ duration: vdu, od: ovdu, price: v.p / 100, resourceId: resource.id })
        } else {
          const vdu = moment(v.e, 'HHmm').diff(moment(v.s, 'HHmm'), 'minutes')
          byStartTime[v.s].durations.push(vdu)
          times.push({ duration: vdu, price: v.p / 100, resourceId: resource.id })
        }
        court.times = times.sort((a, b) => a.duration - b.duration)

        const uniqueDurations = [...new Set(byStartTime[v.s].durations)].sort((a, b) => a - b)
        byStartTime[v.s].durations = uniqueDurations
        byStartTime[v.s].courts.push(court)
      })
    } else if (!members.has(resource.id)) {
      // A member court with no vacancies stays out of the no-vacancy bucket: it would surface as
      // its own fully booked column next to the merged one. The merged court is added below.
      const court = { ...resource, id: resource.id, times: [] }
      if (!byStartTime['0000'])
        byStartTime['0000'] = { courts: [], start: moment.utc(dateindex + 'T0000').format(), durations: [] }
      byStartTime['0000'].courts.push(court)
    }
  }
  _groups.forEach((group) => {
    if (group.members.some((id) => resources[id] && vacancies[id] && vacancies[id].length > 0)) return
    // No member is free all day: the merged court still gets its fully booked column, as any
    // real court would.
    const first = resources[group.members.find((id) => resources[id])]
    const start = moment.utc(dateindex + 'T0000').format()
    if (!byStartTime['0000']) byStartTime['0000'] = { courts: [], start, durations: [] }
    byStartTime['0000'].courts.push(groupCourt(group, first, start, []))
  })
  const byStartTimeArray = mergeMultipleCourts(Object.values(byStartTime), _groups).filter((x) => moment.utc(x.start))
  byStartTimeArray.sort((a, b) => moment.utc(a.start).unix() - moment.utc(b.start).unix())
  const cindex = byStartTimeArray
    .filter((x) => { if (all) { return true } else { return moment.utc(x.start).isAfter(moment.utc().add(TIMEZONEOFFSET, 'minutes'), 'minutes') } })
    .map((x, i) => {
      return { ...x, id: dateindex, key: dateindex + '.' + i }
    })
  const lastminute = byStartTimeArray
    .filter((x) => moment.utc(x.start).isBetween(moment.utc().add(TIMEZONEOFFSET, 'minutes').subtract(30, 'minutes'), moment.utc().add(TIMEZONEOFFSET, 'minutes').add(30, 'minutes'), 'minutes', '[]'))
    .map((x) => {
      return { ...x, id: dateindex, key: dateindex + '.' + x.start }
    })
  return { cindex, lastminute }
}